/**
 * The clients API client — the record, its history, its people, its conversion.
 *
 * A client per module, and not a growing `lib/api.ts` (waves.md §1.1). The
 * `clients` block that lived there until Wave 2A moved here whole; `lib/api.ts`
 * keeps only what every module needs — `api()`, `ApiError`, the org header.
 *
 * Two rules of the ADRs shape every type below, and both are easy to lose in a
 * form:
 *
 * - **The PAN is optional** (ADR-0016). `pan` is `string | null` everywhere,
 *   masked on a list read and full on a single read, and `null` means *this
 *   client has no PAN* — never "we did not load it". `has_pan` is the field to
 *   branch on when the difference matters.
 * - **A client is a kind, not a type** (ADR-0015). `individual` or `business`;
 *   a business has a `constitution`; only a company has a `company_type`. The
 *   three are validated together, here and in the service, because they are
 *   only correct together.
 */

import { GST_STATE_CODES } from '../../routes/Firm/settings/rules'
import { ApiError, api } from '../api'

// -- shapes ------------------------------------------------------------------

export type ClientKind = 'individual' | 'business'

export interface Registration {
  kind: string
  value: string
  label: string | null
}

export interface ClientRecord {
  id: string
  legal_name: string
  display_name: string | null
  kind: ClientKind
  constitution: string | null
  company_type: string | null
  /** Masked (`AAAPA****A`) on a list read, full on a single read, and **null
   *  when the client has no PAN** — not a placeholder. */
  pan: string | null
  has_pan: boolean
  gstin: string | null
  registrations: Registration[]
  tags: string[]
  email: string | null
  mobile: string | null
  address: string | null
  city: string | null
  state: string | null
  pincode: string | null
  branch_id: string | null
  status: 'active' | 'archived'
  converted_at: string | null
  engagement_owner_user_id: string | null
  /**
   * **May an `authorised` person approve a deliverable on this entity?** — spec
   * change C8 §3b, the owner, 2026-09-23; Wave 9F.
   *
   * The firm's own choice, per entity, and it defaults to `false` for every
   * client that existed before the switch did. Nothing in the *client* portal
   * reads this field — the portal is served the resolved verb
   * (`entity.may.approve_document`) rather than the setting behind it, so a
   * button that is drawn and a request that is refused read the same answer.
   * This is here for the one staff control that sets it, on Client 360.
   */
  authorised_may_approve: boolean
  created_at: string
  /**
   * When the record last changed — C27 item 4 (UAT report #20). The edit
   * dialog sends it back as `expected_updated_at`, and the service refuses a
   * save made against an older copy (`409 CONFLICT`, reason `stale`) rather
   * than letting it silently undo somebody else's.
   *
   * Optional because a service from before C27 does not send it, and an edit
   * then goes without the guard rather than not at all.
   */
  updated_at?: string
  /**
   * **Whether this client's documents live in ProDoc** — Wave 28, C-4's I-5
   * (ADR-0055 §6). Served on `GET /api/v1/clients/{id}`.
   *
   * Optional because a service from before Wave 28, or a list read, does not
   * send it, and the documents panel reads its absence as *not moved*.
   */
  prodoc?: ClientProDoc | null
}

/**
 * I-5's `prodoc` on a client read.
 *
 * `enabled` is the service's word that **this org's** documents are written to
 * ProDoc (the org's state `writing` or `moved`; no switch since C28).
 * `folder_id` is the client's own folder there, `Clients/<name>` (ADR-0055 §3),
 * and is `null` until the client's first document creates it.
 */
export interface ClientProDoc {
  enabled: boolean
  folder_id: string | null
}

export interface HistoryEntry {
  id: string
  kind: string
  summary: string
  actor_user_id: string | null
  /** `user` (a member of the firm), `system`, or `client_person` — somebody
   *  acting in the client portal. `actor_user_id` is null for the last two,
   *  which is why the kind is served separately (Wave 5 close, 5D §5.2). */
  actor_kind: string
  at: string
}

/** Who did it, for a trail row. Staff acts are the unlabelled default: the
 *  panel would be a wall of "Firm" badges otherwise, and the two worth calling
 *  out are the ones a firm cannot infer from a name it does not recognise. */
export const ACTOR_LABELS: Record<string, string> = {
  client_person: 'Client portal',
  system: 'Automatic',
}

export interface LinkedPerson {
  person_id: string
  full_name: string
  email: string | null
  mobile: string | null
  status: string
  role: string
  is_self: boolean
  linked_at: string
}

export interface InvitationRow {
  id: string
  person_id: string
  role: string
  status: string
  channel: string | null
  failure_reason: string | null
  at: string
}

/**
 * Client 360 (FR-B7).
 *
 * The four empty collections and the null ledger are **the slots the later
 * waves fill**, and the service serves them empty rather than omitting them so
 * this type does not change when they arrive. A screen that grows a new
 * top-level key per wave is a screen rewritten per wave.
 */
export interface Client360 {
  client: ClientRecord
  people: LinkedPerson[]
  history: HistoryEntry[]
  invitations: InvitationRow[]
  /**
   * **These five are served empty for every client** — spec §9-38, which names
   * only `open_cases` and is in fact all of them. The screen does not read
   * them: it calls the cases, billing, documents, chat and calendar clients
   * itself. They are typed `unknown` because nothing has ever put a shape in
   * them, and typing a shape that is never sent is the lie §9-38 is about.
   */
  open_cases: unknown[]
  ledger: unknown | null
  documents: unknown[]
  conversations: unknown[]
  due_dates: unknown[]
}

export interface Vocabulary {
  kinds: ClientKind[]
  constitutions: string[]
  company_types: string[]
  registration_kinds: string[]
  link_roles: string[]
}

export interface ClientBody {
  legal_name: string
  kind?: ClientKind
  constitution?: string | null
  company_type?: string | null
  pan?: string | null
  display_name?: string | null
  gstin?: string | null
  registrations?: Registration[]
  tags?: string[]
  /** **`null`, not `undefined`, is how a contact detail is cleared.** The
   *  service reads a PATCH with `exclude_unset`, so an omitted key keeps what
   *  is stored and an emptied box has to send something. These four were typed
   *  `string` and the four below did not exist at all until Wave 7A, which is
   *  the shape of the defect: `ClientBody` was written for the *add* dialog and
   *  the add dialog collects none of them. */
  email?: string | null
  mobile?: string | null
  address?: string | null
  city?: string | null
  state?: string | null
  pincode?: string | null
  branch_id?: string | null
  /** C8 §3b — see `ClientRecord.authorised_may_approve`. Optional like every
   *  other key here: the service reads a PATCH with `exclude_unset`, so the
   *  toggle sends only itself and cannot disturb a field it never rendered. */
  authorised_may_approve?: boolean
  /** C27 item 4 — the `updated_at` of the copy the edit was made against. A
   *  PATCH only; the service compares it and refuses a stale save. */
  expected_updated_at?: string
}

// -- the client --------------------------------------------------------------

export const clients = {
  list: (
    params: {
      search?: string
      status?: string
      kind?: string
      tag?: string
      branch_id?: string
      limit?: number
      offset?: number
    } = {},
  ) => {
    const query = new URLSearchParams()
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== '') query.set(key, String(value))
    }
    const suffix = query.toString() ? `?${query}` : ''
    return api<ClientRecord[]>(`/api/v1/clients${suffix}`)
  },
  get: (id: string) => api<ClientRecord>(`/api/v1/clients/${id}`),
  view: (id: string) => api<Client360>(`/api/v1/clients/${id}/360`),
  people: (id: string) => api<LinkedPerson[]>(`/api/v1/clients/${id}/people`),
  history: (id: string) => api<HistoryEntry[]>(`/api/v1/clients/${id}/history`),
  vocabulary: () => api<Vocabulary>('/api/v1/clients/vocabulary'),
  create: (body: ClientBody) => api<ClientRecord>('/api/v1/clients', { method: 'POST', body }),
  update: (id: string, body: Partial<ClientBody>) =>
    api<ClientRecord>(`/api/v1/clients/${id}`, { method: 'PATCH', body }),
  /**
   * Convert in place (FR-B8). **The same id comes back** — nothing moves and
   * nothing is created (ADR-0016 §4), so the screen re-reads this client
   * rather than navigating anywhere.
   *
   * Omitting `pan` keeps the identifier; sending `null` clears it. The dialog
   * must therefore only include the key when the firm actually touched the
   * field, which `convertBody` below is what does.
   */
  convert: (id: string, body: Record<string, unknown>) =>
    api<ClientRecord>(`/api/v1/clients/${id}/convert`, { method: 'POST', body }),
  /**
   * Archive (FR-B1, C27 item 1). **Refused while work is open** — `409
   * CONFLICT`, `details.reason = "work_open"`, with `details.open` counting
   * the open cases, unpaid invoices and active retainers; `openWorkSentence`
   * below turns that into the list the dialog shows.
   */
  archive: (id: string) =>
    api<ClientRecord>(`/api/v1/clients/${id}/archive`, { method: 'POST' }),
  /** Undo an archive (C27 item 1). Refused `409 CONFLICT`, reason
   *  `pan_taken`, when an active client of the firm now holds this PAN. */
  restore: (id: string) =>
    api<ClientRecord>(`/api/v1/clients/${id}/restore`, { method: 'POST' }),
}

// -- the refusals a client's own actions can meet (C27) -----------------------

/** `details.reason` on a `409 CONFLICT` from the client routes. */
export const WORK_OPEN = 'work_open'
export const PAN_TAKEN = 'pan_taken'
export const STALE = 'stale'

/** What `details.open` counts on a `work_open` refusal. */
export interface OpenWork {
  cases?: number
  invoices?: number
  retainers?: number
}

/**
 * "2 open cases, 1 unpaid invoice and 1 active retainer" — the refusal to
 * archive, as a sentence the firm can act on (C27 item 1, UAT report #1).
 *
 * The counts are the service's; a kind it counts as zero is left out rather
 * than said as "0 retainers", and an empty or unreadable `open` says nothing
 * at all, so the caller falls back on the service's own message.
 */
export function openWorkSentence(open: unknown): string | null {
  if (!open || typeof open !== 'object') return null
  const counts = open as OpenWork
  const part = (count: unknown, one: string, many: string) =>
    typeof count === 'number' && count > 0 ? `${count} ${count === 1 ? one : many}` : null
  const parts = [
    part(counts.cases, 'open case', 'open cases'),
    part(counts.invoices, 'unpaid invoice', 'unpaid invoices'),
    part(counts.retainers, 'active retainer', 'active retainers'),
  ].filter((value): value is string => value !== null)
  if (parts.length === 0) return null
  if (parts.length === 1) return parts[0]!
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`
}

// -- pure helpers, so the screens' rules are testable ------------------------

/** Structural checks, mirroring the service's. Shape only — whether a PAN
 *  exists is a question for the income-tax department's verification service,
 *  and that lookup is a domain seam. */
export const PAN_RE = /^[A-Z]{5}[0-9]{4}[A-Z]$/
export const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/

export const KIND_LABELS: Record<string, string> = {
  individual: 'Individual',
  business: 'Business',
}

export const CONSTITUTION_LABELS: Record<string, string> = {
  proprietorship: 'Proprietorship',
  partnership: 'Partnership firm',
  llp: 'LLP',
  company: 'Company',
  huf: 'Hindu undivided family',
  trust: 'Trust',
  society: 'Society',
  aop_boi: 'AOP / BOI',
  other: 'Other',
}

export const COMPANY_TYPE_LABELS: Record<string, string> = {
  private: 'Private limited',
  public: 'Public limited',
  opc: 'One person company',
  section_8: 'Section 8',
}

export const REGISTRATION_LABELS: Record<string, string> = {
  tan: 'TAN',
  iec: 'IEC',
  cin: 'CIN',
  llpin: 'LLPIN',
  bar_enrolment: 'Bar enrolment',
  udyam: 'Udyam',
  other: 'Other',
}

/**
 * What a client *is*, in one phrase, for a table cell and a page heading.
 *
 * One function rather than an inline ternary in each screen: the list, the 360
 * header and the conversion dialog must all say the same thing about the same
 * record, and three copies of the ternary is three chances for one of them to
 * stop saying "Company (private limited)" when the company type changes.
 */
export function describeKind(row: {
  kind: string
  constitution?: string | null
  company_type?: string | null
}): string {
  if (row.kind === 'individual') return KIND_LABELS.individual ?? 'Individual'
  const constitution = row.constitution
    ? (CONSTITUTION_LABELS[row.constitution] ?? row.constitution)
    : 'Business'
  if (row.constitution === 'company' && row.company_type) {
    return `${constitution} (${COMPANY_TYPE_LABELS[row.company_type] ?? row.company_type})`
  }
  return constitution
}

/**
 * What to render in a PAN column.
 *
 * **`null` is a real answer**, and the em dash says so. Returning `''` would
 * make a PAN-less client look like a row that failed to load, and returning
 * the string `'null'` — which is what a bare template literal does — has
 * happened in this estate before.
 */
export function panDisplay(pan: string | null): string {
  return pan ?? '—'
}

/** A business needs a constitution; an individual must not have one. */
export function constitutionRequired(kind: string): boolean {
  return kind === 'business'
}

/** Only a company has a company type. */
export function companyTypeApplies(kind: string, constitution: string | null): boolean {
  return kind === 'business' && constitution === 'company'
}

// -- C27's three rules: the PAN's holder, a name's letter, a GSTIN's state ---

/**
 * The fourth characters a PAN may have, by what the client is — ADR-0057 §1,
 * the table word for word. **One table**, read by the add, edit and convert
 * dialogs alike; the service holds the same one and is the authority.
 *
 * A proprietorship is `P` because it trades on its proprietor's own PAN
 * (ADR-0015). `other` takes any letter the income-tax department issues.
 */
export const PAN_HOLDER_LETTERS: Record<string, readonly string[]> = {
  individual: ['P'],
  proprietorship: ['P'],
  partnership: ['F'],
  llp: ['F'],
  company: ['C'],
  huf: ['H'],
  trust: ['T'],
  aop_boi: ['A', 'B'],
  society: ['A', 'T'],
  other: ['A', 'B', 'C', 'F', 'G', 'H', 'J', 'L', 'P', 'T'],
}

/** What the client is, as the PAN rule's sentence names it. */
const HOLDER_PHRASES: Record<string, string> = {
  individual: 'an individual',
  proprietorship: 'a proprietorship',
  partnership: 'a partnership firm',
  llp: 'an LLP',
  company: 'a company',
  huf: 'a Hindu undivided family',
  trust: 'a trust',
  aop_boi: 'an AOP or BOI',
  society: 'a society',
}

/**
 * The letters a PAN's fourth character may be for this kind and constitution,
 * or `null` when nothing can be said yet — a business whose constitution has
 * not been chosen (the form asks for it separately).
 */
export function panHolderLetters(kind: string, constitution: string | null): readonly string[] | null {
  if (kind === 'individual') return PAN_HOLDER_LETTERS.individual!
  if (!constitution) return null
  return PAN_HOLDER_LETTERS[constitution] ?? null
}

/**
 * Why this PAN cannot be this client's, naming the letter expected — or `null`
 * (ADR-0057 §2; UAT report #11: an individual accepted a company's PAN).
 *
 * Checks **only what the PAN says about its holder**. A blank or misshapen PAN
 * is the shape rule's to refuse, and whether the PAN was ever allotted is
 * still the income-tax department's question (`models/client.py`'s seam).
 */
export function panHolderProblem(
  pan: string | null | undefined,
  kind: string,
  constitution: string | null,
): string | null {
  const clean = (pan ?? '').trim().toUpperCase()
  if (!PAN_RE.test(clean)) return null
  const letters = panHolderLetters(kind, constitution)
  if (!letters) return null
  const got = clean[3]!
  if (letters.includes(got)) return null
  const holder = kind === 'individual' ? 'individual' : (constitution ?? '')
  const phrase = HOLDER_PHRASES[holder]
  if (!phrase) {
    return `The fourth character of a PAN says who holds it, and ${got} is not one the income-tax department issues.`
  }
  const expected = letters.length === 1 ? letters[0] : letters.join(' or ')
  return `The fourth character of a PAN for ${phrase} is ${expected}; this one has ${got}. Check the PAN, or what kind of client this is.`
}

/**
 * Why a name is not one — or `null` (C27 item 1, UAT report #18). **A name
 * must contain a letter**: blank-after-trim and all-digit names are refused,
 * and digits beside letters (*3M India Ltd*) are not. Any script's letters
 * count — a name typed in Devanagari is a name.
 *
 * `required: false` is for a field that may be left empty (the display name),
 * where only a filled-in, letterless value is wrong.
 */
export function nameProblem(value: string, { required = true } = {}): string | null {
  const clean = value.trim()
  if (!clean) return required ? 'A client needs a name.' : null
  return /\p{L}/u.test(clean) ? null : 'A name needs at least one letter — it can’t be only numbers.'
}

/** The list's name for a typed state, matched without regard to case or
 *  spacing; `null` when it names no state. The service's resolver also takes
 *  aliases and codes — what it resolves and this does not is simply not
 *  checked here, and the service still refuses it. */
function knownState(value: string | null | undefined): keyof typeof GST_STATE_CODES | null {
  const key = (value ?? '').trim().replace(/\s+/g, ' ').toLowerCase()
  if (!key) return null
  const names = Object.keys(GST_STATE_CODES) as Array<keyof typeof GST_STATE_CODES>
  return names.find((name) => name.toLowerCase() === key) ?? null
}

/**
 * Why a client's GSTIN cannot be registered in its state — or `null` (C27
 * item 3, UAT report #14). The check the firm's own profile already made, on
 * the same table (`routes/Firm/settings/rules.ts`); asked only when the GSTIN
 * is well-formed and the state is one this table names.
 */
export function gstinStateProblem(
  gstin: string | null | undefined,
  state: string | null | undefined,
): string | null {
  const g = (gstin ?? '').trim().toUpperCase()
  if (!GSTIN_RE.test(g)) return null
  const name = knownState(state)
  if (!name) return null
  const codes = GST_STATE_CODES[name]
  if (codes.includes(g.slice(0, 2))) return null
  return `A GSTIN registered in ${name} begins ${codes[0]}; this one begins ${g.slice(0, 2)}. Check the state, or the GSTIN.`
}

/** The message under a misshapen or someone-else's GSTIN — one sentence for
 *  both dialogs, so neither drifts. */
export const GSTIN_MISMATCH = 'That GSTIN is malformed or belongs to a different PAN.'
export const PAN_SHAPE = 'That does not look like a valid PAN.'

export interface DraftClient {
  legal_name: string
  kind: string
  constitution: string | null
  company_type: string | null
  pan: string
  gstin: string
  /** C27 — the add dialog asks neither; the edit form's rules pass them. */
  display_name?: string
  state?: string
}

/** Each field of a draft that is wrong, and what to say under it. */
export type DraftMessages = Partial<Record<string, string>>

/**
 * What is wrong with a draft, field by field, in the words to show under each.
 * `draftProblems` is its keys.
 *
 * The **PAN rule is the one worth reading**: an empty PAN is valid, and only a
 * partially-typed one is refused. Requiring a well-formed PAN whenever the
 * field is non-empty would be right; requiring it to be non-empty would quietly
 * re-impose the rule ADR-0016 removed, which is exactly what a `required`
 * attribute left on the input would do.
 *
 * C27 adds three, each the service's too: the PAN's fourth character fits the
 * kind (ADR-0057) — for a PAN typed, or the one a GSTIN carries when none was;
 * a name has a letter; a GSTIN begins with its state's code.
 */
export function draftMessages(draft: DraftClient): DraftMessages {
  const out: DraftMessages = {}
  const name = nameProblem(draft.legal_name)
  if (name) out.legal_name = name
  if (draft.display_name !== undefined) {
    const display = nameProblem(draft.display_name, { required: false })
    if (display) out.display_name = display
  }
  if (constitutionRequired(draft.kind) && !draft.constitution) {
    out.constitution = 'Choose what kind of business this is.'
  }

  const pan = draft.pan.trim().toUpperCase()
  if (pan && !PAN_RE.test(pan)) out.pan = PAN_SHAPE
  else {
    const holder = panHolderProblem(pan, draft.kind, draft.constitution)
    if (holder) out.pan = holder
  }

  const gstin = draft.gstin.trim().toUpperCase()
  if (gstin) {
    if (!GSTIN_RE.test(gstin)) out.gstin = GSTIN_MISMATCH
    // A GSTIN embeds the PAN at characters 3–12. One that embeds a different
    // PAN belongs to somebody else — and when no PAN was typed, this one fills
    // it (ADR-0016 §3), so there is nothing to disagree with.
    else if (pan && gstin.slice(2, 12) !== pan) out.gstin = GSTIN_MISMATCH
    else {
      // The PAN a GSTIN fills in passes the same holder rule (ADR-0057,
      // Consequences); with a PAN typed, the PAN's own message has said it.
      const holder = pan ? null : panHolderProblem(gstin.slice(2, 12), draft.kind, draft.constitution)
      const state = gstinStateProblem(gstin, draft.state)
      if (holder) out.gstin = holder
      else if (state) out.gstin = state
    }
  }
  return out
}

/** Whether the form may be submitted, and which fields are wrong. */
export function draftProblems(draft: DraftClient): string[] {
  return Object.keys(draftMessages(draft))
}

/**
 * Which field a service refusal belongs under — C27: a `VALIDATION_ERROR`'s
 * `details.field` (`pan`, `legal_name`, `display_name`, `gstin`, …) lands
 * under that field rather than in the dialog's banner, so the firm sees the
 * service's words where the mistake is.
 */
export function refusedField(err: unknown): { field: string; message: string } | null {
  if (!(err instanceof ApiError) || err.code !== 'VALIDATION_ERROR') return null
  const field = err.details.field
  return typeof field === 'string' && field ? { field, message: err.message } : null
}

/** The `details.reason` of a `409 CONFLICT`, or `null`. */
export function conflictReason(err: unknown): string | null {
  if (!(err instanceof ApiError) || err.code !== 'CONFLICT') return null
  const reason = err.details.reason
  return typeof reason === 'string' ? reason : null
}

/**
 * The body for a conversion, with **only the identifier keys the firm touched**.
 *
 * The service reads `exclude_unset`: an omitted `pan` keeps the client's PAN,
 * an explicit `null` clears it. So a dialog that always sent `pan` would clear
 * the PAN of every client converted without the field being filled in — during
 * the one operation most likely to be examined again months later.
 */
export function convertBody(
  next: { kind: string; constitution: string | null; company_type: string | null },
  identifiers: { panTouched: boolean; pan: string; gstinTouched: boolean; gstin: string },
): Record<string, unknown> {
  const body: Record<string, unknown> = {
    kind: next.kind,
    constitution: next.constitution,
    company_type: next.company_type,
  }
  if (identifiers.panTouched) body.pan = identifiers.pan.trim().toUpperCase() || null
  if (identifiers.gstinTouched) body.gstin = identifiers.gstin.trim().toUpperCase() || null
  return body
}

/** The history entry's icon-ish tone. Kept beside the labels so an unknown
 *  `kind` — every later wave adds its own words — renders as neutral rather
 *  than as nothing. */
export function historyTone(kind: string): 'created' | 'converted' | 'people' | 'neutral' {
  if (kind === 'created') return 'created'
  if (kind === 'converted') return 'converted'
  if (kind.startsWith('person_')) return 'people'
  return 'neutral'
}

// -- the edit screen's rules — ACCEPTANCE §3.1 -------------------------------

/**
 * What the edit form holds. **Strings, never nulls**, because an input's value
 * is a string and `value={null}` is React's uncontrolled-input warning; the
 * nulls come back at the boundary in `editBody`.
 *
 * It is deliberately not `DraftClient` plus fields. `kind`, `constitution` and
 * `company_type` are absent: changing what a client *is* is a conversion —
 * audited, published and its own route — and an edit form that offered the
 * three would be a second, unaudited way to do it.
 */
export interface ClientEdit {
  legal_name: string
  display_name: string
  pan: string
  gstin: string
  email: string
  mobile: string
  address: string
  city: string
  state: string
  pincode: string
  branch_id: string
  tags: string
  registrations: Registration[]
}

/** The record as the form holds it. */
export function editFrom(client: ClientRecord): ClientEdit {
  return {
    legal_name: client.legal_name,
    display_name: client.display_name ?? '',
    pan: client.pan ?? '',
    gstin: client.gstin ?? '',
    email: client.email ?? '',
    mobile: client.mobile ?? '',
    address: client.address ?? '',
    city: client.city ?? '',
    state: client.state ?? '',
    pincode: client.pincode ?? '',
    branch_id: client.branch_id ?? '',
    tags: client.tags.join(', '),
    registrations: client.registrations.map((row) => ({ ...row })),
  }
}

/**
 * Whether the edit may be saved, and which fields are wrong.
 *
 * The **PAN rule is `draftProblems`'s**, and for the same reason (ADR-0016): an
 * empty PAN is a valid answer and only a partially-typed one is refused. The
 * identifier rules are shared with the add dialog rather than re-derived, so
 * the two forms cannot come to disagree about what a valid GSTIN is — which is
 * the failure mode a second copy of a regex has.
 *
 * A registration row with a kind and no value is refused: it is a row somebody
 * started and abandoned, and storing it would put an empty TAN on the record.
 */
export function editProblems(edit: ClientEdit, client?: ClientRecord): string[] {
  return Object.keys(editMessages(edit, client))
}

/**
 * `editProblems` with the words to show under each field.
 *
 * `client` is the record the dialog opened with. It supplies what the client
 * *is* — the PAN's fourth character is judged against the stored kind and
 * constitution (ADR-0057), since neither is editable here — and it decides
 * which C27 rules apply: **a rule is asked only of what this edit changes**.
 * A client already stored with a mismatching PAN or a letterless name keeps it
 * until somebody edits that field (ADR-0057 §3); an edit to the email address
 * must not be refused over a PAN nobody touched. The service reads the same
 * way, because the body carries only the changed fields.
 *
 * Without `client` no kind is known, so the holder rule is not asked.
 */
export function editMessages(edit: ClientEdit, client?: ClientRecord): DraftMessages {
  const out = draftMessages({
    legal_name: edit.legal_name,
    display_name: edit.display_name,
    // The kind is not editable here. `business` with no constitution is the
    // value that asks the holder rule nothing; the constitution message it
    // raises belongs to the add dialog and is dropped below.
    kind: client?.kind ?? 'business',
    constitution: client ? client.constitution : null,
    company_type: null,
    pan: edit.pan,
    gstin: edit.gstin,
    state: edit.state,
  })
  delete out.constitution
  if (client) {
    const changed = new Set(Object.keys(editBody(edit, editFrom(client))))
    // A blank name is refused whatever it was; the letter rule waits for a change.
    if (!changed.has('legal_name') && edit.legal_name.trim()) delete out.legal_name
    if (!changed.has('display_name')) delete out.display_name
    if (!changed.has('pan')) delete out.pan
    if (!changed.has('gstin') && !changed.has('state') && !changed.has('pan')) delete out.gstin
  }
  if (edit.registrations.some((row) => !row.value.trim())) {
    out.registrations = 'A registration needs a number, or remove the row.'
  }
  return out
}

/**
 * The PATCH body — **only the fields this edit changed**, when `from` (the
 * form as it opened, `editFrom(client)`) is given. C27 item 4, UAT report #20:
 * the dialog used to send every field it showed, so two people editing the
 * same client in turn had the second silently put back everything the first
 * had changed. A key the edit did not touch is now omitted, and the service's
 * `exclude_unset` keeps what is stored for it.
 *
 * **An emptied field is still sent, as `null`** — the rule this function was
 * written for and the opposite of `convertBody`'s. A firm clearing the mobile
 * number and pressing Save must actually clear it; a body that only carried
 * non-empty values would be a form in which nothing can ever be deleted.
 *
 * Without `from`, every field is sent (the whole form, normalised).
 *
 * `kind`, `constitution` and `company_type` are never in the body; the service
 * refuses them on this route anyway (`ClientPatch` has no such fields), and the
 * two halves agreeing is ADR-0029's "both, always". `expected_updated_at` is
 * the dialog's to add — it is about the copy, not the form.
 */
export function editBody(edit: ClientEdit, from?: ClientEdit): Partial<ClientBody> {
  const body = wholeEditBody(edit)
  if (!from) return body
  const before = wholeEditBody(from) as Record<string, unknown>
  const changed: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(body)) {
    if (JSON.stringify(value) !== JSON.stringify(before[key])) changed[key] = value
  }
  return changed as Partial<ClientBody>
}

function wholeEditBody(edit: ClientEdit): Partial<ClientBody> {
  const blank = (value: string) => value.trim() || null
  return {
    legal_name: edit.legal_name.trim(),
    display_name: blank(edit.display_name),
    pan: edit.pan.trim().toUpperCase() || null,
    gstin: edit.gstin.trim().toUpperCase() || null,
    email: blank(edit.email),
    mobile: blank(edit.mobile),
    address: blank(edit.address),
    city: blank(edit.city),
    state: blank(edit.state),
    pincode: blank(edit.pincode),
    branch_id: edit.branch_id || null,
    tags: edit.tags
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean),
    registrations: edit.registrations
      .filter((row) => row.value.trim())
      .map((row) => ({
        kind: row.kind,
        value: row.value.trim(),
        label: row.label?.trim() || null,
      })),
  }
}
